import React from 'react';
import { schema, Parameters } from '../schema';

export type StaticProps = {
  referenceData: {
    pageSizes: number[];
  };
  defaultParameters: Parameters;
};

export type ApplicationExportPageInitialProps = {
  referenceData: {
    pageSizes: number[];
  };
  defaultParameters: {
    pageSize: number;
    pageNumber: number;
  };
};

const staticPropsContext = React.createContext<StaticProps | undefined>(undefined);

export interface StaticPropsContextProps {
  initialProps: ApplicationExportPageInitialProps;
  children: React.ReactNode;
}

export function StaticPropsContextProvider(props: StaticPropsContextProps) {
  const value = React.useMemo(
    () => ({
      referenceData: props.initialProps.referenceData,
      defaultParameters: getDefaultQueryParameters(props.initialProps),
    }),
    [props.initialProps]
  );
  return <staticPropsContext.Provider value={value}>{props.children}</staticPropsContext.Provider>;
}

export function useStaticProps() {
  const value = React.useContext(staticPropsContext);
  if (value === undefined) {
    throw new Error('useStaticProps must be used within a StaticPropsContextProvider');
  }
  return value;
}

function getDefaultQueryParameters({ defaultParameters }: ApplicationExportPageInitialProps) {
  type PartialRecord<K extends keyof Parameters, T> = { [P in K]?: T };
  const defaultValues: PartialRecord<keyof Parameters, unknown> = {};
  schema.parameters.forEach((parameter) => {
    const value = parameter.getDefaultValue({
      defaultParameters: {
        pageNumber: defaultParameters.pageNumber,
        pageSize: defaultParameters.pageSize,
      },
    });
    defaultValues[parameter.key] = value;
  });
  return defaultValues as Parameters;
}
